import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Btn, Modal, Input, Select } from "@/components/UI";
import { Star, StarOff, Search, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, XCircle, HelpCircle, Filter } from "lucide-react";
import { usePagination, ListPagination } from "@/components/ListPagination";

const LEVEL_STYLE = {
  GREEN:  { label: "Yüksek Uygunluk",     cls: "bg-emerald-50 text-emerald-800 border-emerald-300", dot: "bg-emerald-500" },
  YELLOW: { label: "İnceleme Gerekli",    cls: "bg-amber-50 text-amber-800 border-amber-300",       dot: "bg-amber-500" },
  RED:    { label: "Uygun Görünmüyor",    cls: "bg-rose-50 text-rose-800 border-rose-300",          dot: "bg-rose-500" },
};

const STATUS_LABEL = {
  ACTIVE: "Aktif", UPCOMING: "Yaklaşan", EXPIRED: "Süresi Dolmuş",
};

const AREA_LABEL = {
  machine: "Makine", rd: "Ar-Ge", digital: "Dijitalleşme", export: "İhracat",
  employment: "İstihdam", energy: "Enerji", green: "Yeşil Dönüşüm",
  capacity: "Kapasite", international: "Uluslararasılaşma",
};

export default function SupportsPage() {
  const [profile, setProfile] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [detailCode, setDetailCode] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [tab, setTab] = useState("all"); // all | followed

  // Ilk yükleme + kullanıcı tarayınca fetch. Filtre değişimi CLIENT-SIDE — arka
  // planda otomatik yeniden çağrı YOK. Böylece Destekler sayfası hiçbir zaman
  // kendi kendine tarama başlatmaz.
  const load = async () => {
    if (loading) return;   // duplicate-click guard (ör. çift Tara sonrası load)
    setLoading(true);
    try {
      const r = await api.get(`/supports/programs`);
      setList(r);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    api.get("/supports/company-profile").then(p => { if (alive) setProfile(p); }).catch(() => { if (alive) setProfile({}); });
    load();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scan = async () => {
    if (scanning) return;   // click-through guard
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.post("/supports/scan");
      setScanResult(res);
      await load();
    } catch (e) {
      alert("Tarama hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setScanning(false); }
  };

  const rows = list?.rows || [];
  const summary = list?.summary || {};
  // Client-side filtering — no extra API calls on filter change.
  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (filters.level && r.suitability_level !== filters.level) return false;
      if (filters.kind && r.kind !== filters.kind) return false;
      if (filters.area && !((r.areas || []).includes(filters.area) || r.area === filters.area)) return false;
      if (filters.status && r.status_bucket !== filters.status) return false;
      return true;
    });
  }, [rows, filters]);
  const followedRows = filteredRows.filter(r => r.followed);
  const shownRows = tab === "followed" ? followedRows : filteredRows;
  const pg = usePagination(shownRows, "supports", 100);

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <PageHeader title="Destekler" description="Hibe, kredi ve teşvik programları — resmî kaynaklı" />
        <div className="flex gap-2 mt-2">
          <Btn variant="secondary" onClick={() => setProfileOpen(true)} data-testid="supports-profile-btn">
            Firma Profili
          </Btn>
          <Btn onClick={scan} disabled={scanning} data-testid="supports-scan-btn"
               className="bg-emerald-600 text-white hover:bg-emerald-700">
            {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
            Destekleri Tara
          </Btn>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <SummaryCard label="Uygun Aktif Destek" value={summary.suitable_active || 0} tone="emerald" testid="sup-sum-active" />
        <SummaryCard label="Takip Ettiklerim" value={summary.followed || 0} tone="sky" testid="sup-sum-followed" />
        <SummaryCard label="Değişiklik Olan Takipler" value={summary.followed_with_changes || 0} tone="amber" testid="sup-sum-changes" />
        <SummaryCard label="Yaklaşan Son Tarih" value={summary.upcoming_deadlines || 0} tone="violet" testid="sup-sum-upcoming" />
        <SummaryCard label="Son Tarama"
                     value={summary.last_scan_at ? summary.last_scan_at.slice(0, 16).replace("T", " ") : "—"}
                     tone="slate" small testid="sup-sum-last-scan" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 border-b border-slate-200">
        <TabBtn active={tab === "all"} onClick={() => setTab("all")} testid="sup-tab-all">Tüm Destekler ({rows.length})</TabBtn>
        <TabBtn active={tab === "followed"} onClick={() => setTab("followed")} testid="sup-tab-followed">Takip Ettiklerim ({followedRows.length})</TabBtn>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <Filter size={14} className="text-slate-500" />
        <FilterPill options={[["","Uygunluk"],["GREEN","🟢 Yüksek"],["YELLOW","🟡 İncelenmeli"],["RED","🔴 Uygun Değil"]]}
                    value={filters.level || ""} onChange={v => setFilters({...filters, level: v})}
                    testid="sup-filter-level" />
        <FilterPill options={[["","Tür"],["GRANT","Hibe"],["INCENTIVE","Teşvik"],["LOAN","Kredi"]]}
                    value={filters.kind || ""} onChange={v => setFilters({...filters, kind: v})}
                    testid="sup-filter-kind" />
        <FilterPill options={[["","Alan"],["rd","Ar-Ge"],["machine","Makine"],["digital","Dijitalleşme"],["export","İhracat"],["green","Yeşil Dönüşüm"],["capacity","Kapasite"]]}
                    value={filters.area || ""} onChange={v => setFilters({...filters, area: v})}
                    testid="sup-filter-area" />
        <FilterPill options={[["","Durum"],["ACTIVE","Aktif"],["UPCOMING","Yaklaşan"],["EXPIRED","Süresi Dolmuş"]]}
                    value={filters.status || ""} onChange={v => setFilters({...filters, status: v})}
                    testid="sup-filter-status" />
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-2 text-left">Destek</th>
                <th className="p-2 text-left">Kurum</th>
                <th className="p-2 text-center">Tür</th>
                <th className="p-2 text-left">Tutar</th>
                <th className="p-2 text-center">Uygunluk</th>
                <th className="p-2 text-center">Son Tarih</th>
                <th className="p-2 text-center">Durum</th>
                <th className="p-2 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-6 text-center text-slate-400"><RefreshCw className="inline animate-spin" size={16} /></td></tr>}
              {!loading && shownRows.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400 text-sm italic">
                  Kayıt bulunamadı. "Destekleri Tara" ile yenileyebilirsiniz.
                </td></tr>
              )}
              {!loading && pg.paged.map(r => {
                const st = LEVEL_STYLE[r.suitability_level] || LEVEL_STYLE.YELLOW;
                return (
                  <tr key={r.code} className="border-t hover:bg-slate-50/70 cursor-pointer"
                      onClick={() => setDetailCode(r.code)}
                      data-testid={`sup-row-${r.code}`}>
                    <td className="p-2">
                      <div className="font-medium flex items-center gap-1">
                        {r.followed && <Star size={12} className="text-amber-500 fill-amber-500" />}
                        {r.has_change_event && r.followed && (
                          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">GÜNCELLENDİ</span>
                        )}
                        {r.name}
                      </div>
                    </td>
                    <td className="p-2 text-slate-600 text-xs">{r.institution}</td>
                    <td className="p-2 text-center text-xs">{r.kind}</td>
                    <td className="p-2 text-slate-700 text-xs">{r.amount_display}</td>
                    <td className="p-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${st.cls}`}>
                        <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                    </td>
                    <td className="p-2 text-center text-xs font-mono">
                      {r.deadline || (r.deadline_note ? <span className="text-slate-500 italic">Çağrı bazlı</span> : "—")}
                    </td>
                    <td className="p-2 text-center text-xs">{STATUS_LABEL[r.status_bucket] || r.status_bucket}</td>
                    <td className="p-2 text-center">
                      <button className="text-xs text-emerald-700 hover:underline">Detay →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <ListPagination pagination={pg} testid="supports-pagination"/>

      {profileOpen && (
        <CompanyProfileModal
          initial={profile || {}}
          onClose={() => setProfileOpen(false)}
          onSaved={(p) => { setProfile(p); setProfileOpen(false); load(); }}
        />
      )}
      {scanResult && (
        <ScanResultModal result={scanResult} onClose={() => setScanResult(null)} />
      )}
      {detailCode && (
        <ProgramDetailModal
          code={detailCode}
          onClose={() => setDetailCode(null)}
          onFollowChanged={() => load()}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${active ? "border-emerald-600 text-emerald-700 bg-emerald-50/40" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
      {children}
    </button>
  );
}

function SummaryCard({ label, value, tone, small, testid }) {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50/50 text-emerald-800",
    sky: "border-sky-200 bg-sky-50/50 text-sky-800",
    amber: "border-amber-200 bg-amber-50/50 text-amber-800",
    violet: "border-violet-200 bg-violet-50/50 text-violet-800",
    slate: "border-slate-200 bg-slate-50/50 text-slate-700",
  };
  return (
    <div className={`p-3 border rounded ${toneMap[tone] || toneMap.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className={`${small ? "text-sm font-mono" : "text-2xl font-bold"} mt-1`}>{value}</div>
    </div>
  );
}

function FilterPill({ options, value, onChange, testid }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
            data-testid={testid}
            className="text-xs px-2 py-1 border border-slate-200 rounded bg-white">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function CompanyProfileModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    company_name: initial.company_name || "",
    sector: initial.sector || "",
    nace_code: initial.nace_code || "",
    city: initial.city || "",
    employee_count: initial.employee_count ?? "",
    is_sme: initial.is_sme ?? null,
    is_exporter: initial.is_exporter ?? null,
    has_rd_activity: initial.has_rd_activity ?? null,
    is_manufacturer: initial.is_manufacturer ?? null,
    kosgeb_registered: initial.kosgeb_registered ?? null,
    interest_areas: initial.interest_areas || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleArea = (a) => {
    const set = new Set(f.interest_areas);
    if (set.has(a)) set.delete(a); else set.add(a);
    setF({ ...f, interest_areas: [...set] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        employee_count: f.employee_count === "" ? null : Number(f.employee_count),
      };
      const r = await api.put("/supports/company-profile", payload);
      onSaved(r);
    } catch (e) {
      alert("Kayıt hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Firma Profili" size="lg">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Firma Adı" value={f.company_name} onChange={e => setF({...f, company_name: e.target.value})}
               data-testid="profile-company-name" />
        <Input label="Sektör / Faaliyet Alanı" value={f.sector} onChange={e => setF({...f, sector: e.target.value})}
               data-testid="profile-sector" />
        <Input label="NACE Kodu" value={f.nace_code} onChange={e => setF({...f, nace_code: e.target.value})}
               data-testid="profile-nace" placeholder="20.42.01" />
        <Input label="Şehir" value={f.city} onChange={e => setF({...f, city: e.target.value})}
               data-testid="profile-city" />
        <Input label="Çalışan Sayısı" type="number" value={f.employee_count}
               onChange={e => setF({...f, employee_count: e.target.value})}
               data-testid="profile-employee-count" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
        <TriToggle label="KOBİ mi?" value={f.is_sme} onChange={v => setF({...f, is_sme: v})} testid="profile-sme" />
        <TriToggle label="İhracat var mı?" value={f.is_exporter} onChange={v => setF({...f, is_exporter: v})} testid="profile-exporter" />
        <TriToggle label="Ar-Ge var mı?" value={f.has_rd_activity} onChange={v => setF({...f, has_rd_activity: v})} testid="profile-rd" />
        <TriToggle label="İmalatçı mı?" value={f.is_manufacturer} onChange={v => setF({...f, is_manufacturer: v})} testid="profile-manufacturer" />
        <TriToggle label="KOSGEB kayıt?" value={f.kosgeb_registered} onChange={v => setF({...f, kosgeb_registered: v})} testid="profile-kosgeb" />
      </div>
      <div className="mt-4">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Yatırım İlgi Alanları</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(AREA_LABEL).map(([k, l]) => (
            <button key={k} onClick={() => toggleArea(k)}
                    data-testid={`profile-area-${k}`}
                    className={`px-3 py-1 text-xs rounded-full border font-medium transition ${f.interest_areas.includes(k) ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={save} disabled={saving} data-testid="profile-save">Kaydet</Btn>
      </div>
    </Modal>
  );
}

function TriToggle({ label, value, onChange, testid }) {
  const btn = (v, l) => (
    <button onClick={() => onChange(v)}
            data-testid={`${testid}-${v === null ? "unknown" : v ? "yes" : "no"}`}
            className={`px-2 py-1 text-xs rounded border ${value === v ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}>
      {l}
    </button>
  );
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex gap-1">
        {btn(true, "Evet")}
        {btn(false, "Hayır")}
        {btn(null, "Bilinmiyor")}
      </div>
    </div>
  );
}

function ProgramDetailModal({ code, onClose, onFollowChanged }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/supports/programs/${code}`).then(r => { setD(r); setLoading(false); }).catch(() => setLoading(false));
  }, [code]);

  const toggleFollow = async () => {
    if (d.followed) {
      await api.del(`/supports/programs/${code}/follow`);
    } else {
      await api.post(`/supports/programs/${code}/follow`);
    }
    const r = await api.get(`/supports/programs/${code}`);
    setD(r);
    onFollowChanged && onFollowChanged();
  };

  if (loading) return <Modal open={true} onClose={onClose} title="Yükleniyor..." size="lg"><div className="p-8 text-center"><RefreshCw className="animate-spin inline" /></div></Modal>;
  if (!d) return null;
  const s = d.suitability || {};
  const st = LEVEL_STYLE[s.level] || LEVEL_STYLE.YELLOW;

  return (
    <Modal open={true} onClose={onClose} title={d.name} size="lg">
      <div className={`p-3 mb-3 border rounded ${st.cls}`} data-testid="sup-detail-suitability">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${st.dot}`} />
          <span className="font-semibold">{st.label}</span>
        </div>
        {s.hard_blockers?.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-semibold uppercase mb-1">Kritik Engel</div>
            {s.hard_blockers.map((h, i) => (
              <div key={i} data-testid={`sup-detail-blocker-${i}`}
                   className="text-xs py-0.5 flex items-start gap-1">
                <XCircle size={12} className="mt-0.5 shrink-0" /> {h}
              </div>
            ))}
          </div>
        )}
        {s.reasons_positive?.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-semibold uppercase mb-1">Neden Bize Uygun?</div>
            {s.reasons_positive.map((r, i) => (
              <div key={i} className="text-xs py-0.5 flex items-start gap-1">
                <CheckCircle2 size={12} className="mt-0.5 shrink-0" /> {r}
              </div>
            ))}
          </div>
        )}
        {s.verify_items?.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-semibold uppercase mb-1">Kontrol Edilmeli</div>
            {s.verify_items.map((v, i) => (
              <div key={i} className="text-xs py-0.5 flex items-start gap-1">
                <HelpCircle size={12} className="mt-0.5 shrink-0" /> {v}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <KV label="Kurum" value={d.institution} />
        <KV label="Destek Türü" value={d.kind} />
        <KV label="Destek Miktarı" value={d.amount_display} />
        <KV label="Son Başvuru" value={d.deadline || d.deadline_note || "—"} />
      </div>

      <div className="mt-4">
        <SectionTitle>Desteklenen Harcamalar</SectionTitle>
        <div className="text-xs space-y-0.5">
          {(d.supported_expenses || []).map((x, i) => (
            <div key={i} className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> {x}</div>
          ))}
          {(d.unsupported_expenses || []).map((x, i) => (
            <div key={i} className="flex items-center gap-1 text-slate-500"><XCircle size={12} className="text-rose-500" /> {x}</div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <SectionTitle>Başvuru İçin Gerekenler</SectionTitle>
        {(d.required_documents_hint || []).length > 0 ? (
          <div className="text-xs space-y-0.5">
            {d.required_documents_hint.map((x, i) => (
              <div key={i} data-testid={`sup-detail-req-${i}`}
                   className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> {x}</div>
            ))}
            <div className="text-[10px] italic text-slate-500 mt-1">
              Başvuru belgeleri resmî çağrı dokümanından teyit edilmelidir.
            </div>
          </div>
        ) : (
          <div className="text-xs italic text-slate-500">Başvuru belgeleri resmî çağrı dokümanından teyit edilmelidir.</div>
        )}
      </div>

      {(d.change_events || []).length > 0 && (
        <div className="mt-4">
          <SectionTitle>Son Değişiklikler</SectionTitle>
          {d.change_events.slice(-3).map((c, i) => (
            <div key={i} className="text-xs border-l-2 border-amber-400 pl-2 my-1">
              <div className="text-slate-500">{(c.at || "").slice(0, 16).replace("T", " ")}</div>
              {(c.diffs || []).map((diff, j) => (
                <div key={j} className="text-slate-700">
                  <b>{diff.field}:</b> <span className="line-through text-rose-600">{String(diff.old || "—")}</span> → <span className="text-emerald-700">{String(diff.new || "—")}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap gap-2 items-center">
        <div className="text-xs text-slate-500">
          Son doğrulama: <span className="font-mono">{(d.last_checked_at || "").slice(0, 16).replace("T", " ") || "—"}</span>
        </div>
        <Btn variant="secondary" onClick={toggleFollow} data-testid="sup-detail-follow">
          {d.followed ? <><Star size={14} className="fill-current" /> Takipte</> : <><StarOff size={14} /> Takibe Al</>}
        </Btn>
        <a href={d.source_url} target="_blank" rel="noopener noreferrer"
           data-testid="sup-detail-source"
           className="ml-auto inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline">
          <ExternalLink size={14} /> Resmî Kaynağa Git
        </a>
      </div>
    </Modal>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-medium text-slate-800">{value || "—"}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 mt-2">{children}</div>;
}

function ScanResultModal({ result, onClose }) {
  const r = result || {};
  const reachedNames = r.sources_reached_names || [];
  const unreached = r.sources_unreached || [];
  return (
    <Modal open={true} onClose={onClose} title="Tarama Tamamlandı" size="md">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm" data-testid="scan-result-modal">
        <ScanStat label="Resmî kaynak" value={r.official_sources || 0} testid="scan-sources-total" />
        <ScanStat label="Erişilen kaynak" value={r.sources_reached || 0} tone="emerald" testid="scan-sources-reached" />
        <ScanStat label="Kontrol edilen program" value={r.checked_programs || 0} testid="scan-programs-checked" />
        <ScanStat label="Yeni bulunan" value={r.new_found || 0} tone="emerald" testid="scan-new-found" />
        <ScanStat label="Güncellenen" value={r.updated || 0} tone="amber" testid="scan-updated" />
        <ScanStat label="Değişiklik yok" value={r.unchanged || 0} tone="slate" testid="scan-unchanged" />
        <ScanStat label="Doğrulanamayan" value={r.unverified || 0} tone="rose" testid="scan-unverified" />
        <ScanStat label="Kısmen doğrulanan" value={r.partially_verified || 0} tone="amber" testid="scan-partial" />
        <ScanStat label="Süre (ms)" value={r.duration_ms || 0} tone="slate" testid="scan-duration" />
      </div>

      {reachedNames.length > 0 && (
        <div className="mt-4">
          <SectionTitle>Erişilen Kaynaklar</SectionTitle>
          <div className="flex flex-wrap gap-1">
            {reachedNames.map(n => (
              <span key={n} className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">{n}</span>
            ))}
          </div>
        </div>
      )}

      {unreached.length > 0 && (
        <div className="mt-4">
          <SectionTitle>Erişilemeyen Kaynaklar</SectionTitle>
          <div className="space-y-1">
            {unreached.map((u, i) => (
              <div key={i} data-testid={`scan-unreached-${i}`} className="text-xs text-slate-600 border-l-2 border-amber-400 pl-2">
                <b>{u.provider}</b> — {u.institution}
                <div className="text-slate-500">{(u.errors || []).join(" · ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Btn onClick={onClose} data-testid="scan-result-close">Kapat</Btn>
      </div>
    </Modal>
  );
}

function ScanStat({ label, value, tone, testid }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`p-2 border rounded ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}
